import { Phone, Mail, MapPin, Clock, MessageCircle, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { useState } from 'react';
import { toast } from 'sonner';

const PHONE_NUMBER = "+919971231812";
const WHATSAPP_NUMBER = "919971231812";

export const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Create WhatsApp message with form data
    const whatsappMessage = encodeURIComponent(
      `Hi! I'm ${formData.name}.\nPhone: ${formData.phone}\nMessage: ${formData.message}`
    );
    
    // Simulate form submission
    setTimeout(() => {
      setIsSubmitting(false);
      toast.success('Redirecting to WhatsApp...');
      window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${whatsappMessage}`, '_blank');
      setFormData({ name: '', phone: '', message: '' });
    }, 500);
  };

  return (
    <main data-testid="contact-page" className="bg-[#FDFBF7]">
      {/* Hero Section */}
      <section className="py-12 md:py-20 bg-gradient-to-br from-[#FDFBF7] via-[#F0FDFA] to-[#CCFBF1]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-sm font-medium tracking-widest uppercase text-teal-700 mb-3">
            Get In Touch
          </p>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-stone-800 mb-6">
            Contact Us
          </h1>
          <p className="text-lg text-stone-600 max-w-2xl mx-auto">
            Have questions? We're here to help! Visit our store, call us, or send us a message.
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-12 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            
            {/* Contact Info */}
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-stone-800 mb-8">
                Visit Our Store
              </h2>
              
              <div className="space-y-6">
                {/* Address */}
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-teal-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-stone-800 mb-1">Address</h3>
                    <p className="text-stone-600">
                      Shop No. 72/5, Yusuf Sarai Market,<br />
                      New Delhi, Delhi 110016, India
                    </p>
                    <a 
                      href="https://maps.app.goo.gl/tLGnG1nKfKTFJZHaA"
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="contact-directions-link"
                      className="text-teal-600 hover:text-teal-700 font-medium text-sm mt-2 inline-block"
                    >
                      Get Directions →
                    </a>
                  </div>
                </div>

                {/* Phone */}
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-teal-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-stone-800 mb-1">Phone</h3>
                    <a 
                      href={`tel:${PHONE_NUMBER}`}
                      data-testid="contact-phone-link"
                      className="text-stone-600 hover:text-teal-600 transition-colors"
                    >
                      +91 99712 31812
                    </a>
                  </div>
                </div>

                {/* WhatsApp */}
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#25D366]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <MessageCircle className="w-6 h-6 text-[#25D366]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-stone-800 mb-1">WhatsApp</h3>
                    <a 
                      href={`https://wa.me/${WHATSAPP_NUMBER}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="contact-whatsapp-link"
                      className="text-stone-600 hover:text-[#25D366] transition-colors"
                    >
                      Chat with us on WhatsApp
                    </a>
                  </div>
                </div>

                {/* Hours */}
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-amber-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-stone-800 mb-1">Business Hours</h3>
                    <p className="text-stone-600">
                      Open Daily: 10:30 AM - 9:30 PM<br />
                      <span className="text-sm text-stone-500">Including Sundays & Holidays</span>
                    </p>
                  </div>
                </div>
              </div>

              {/* Quick Action Buttons */}
              <div className="mt-10 flex flex-col sm:flex-row gap-4">
                <a href={`tel:${PHONE_NUMBER}`} className="flex-1">
                  <Button 
                    size="lg"
                    className="w-full rounded-full bg-teal-600 hover:bg-teal-700"
                    data-testid="contact-call-btn"
                  >
                    <Phone className="w-5 h-5 mr-2" />
                    Call Now
                  </Button>
                </a>
                <a 
                  href={`https://wa.me/${WHATSAPP_NUMBER}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1"
                >
                  <Button 
                    size="lg"
                    variant="outline"
                    className="w-full rounded-full border-[#25D366] text-[#25D366] hover:bg-[#25D366] hover:text-white"
                    data-testid="contact-whatsapp-btn"
                  >
                    <MessageCircle className="w-5 h-5 mr-2" />
                    WhatsApp Us
                  </Button>
                </a>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-white rounded-3xl p-6 md:p-10 shadow-sm border border-stone-100">
              <h2 className="text-2xl md:text-3xl font-bold text-stone-800 mb-2">
                Send us a Message
              </h2>
              <p className="text-stone-600 mb-8">
                Fill out the form and we'll get back to you via WhatsApp
              </p>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">
                    Your Name
                  </label>
                  <Input 
                    type="text"
                    placeholder="Enter your name"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    required
                    data-testid="contact-form-name"
                    className="rounded-xl"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">
                    Phone Number
                  </label>
                  <Input 
                    type="tel"
                    placeholder="Enter your phone number"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    required
                    data-testid="contact-form-phone"
                    className="rounded-xl"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">
                    Message
                  </label>
                  <Textarea 
                    placeholder="What are you looking for? (e.g., Sleepwell mattress, bedsheets, etc.)"
                    value={formData.message}
                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                    required
                    rows={4}
                    data-testid="contact-form-message"
                    className="rounded-xl"
                  />
                </div>

                <Button 
                  type="submit"
                  size="lg"
                  disabled={isSubmitting}
                  data-testid="contact-form-submit"
                  className="w-full rounded-full bg-teal-600 hover:bg-teal-700"
                >
                  {isSubmitting ? (
                    'Sending...'
                  ) : (
                    <>
                      <Send className="w-5 h-5 mr-2" />
                      Send via WhatsApp
                    </>
                  )}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="pb-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-8">
          <h2 className="text-2xl md:text-3xl font-bold text-stone-800 mb-4 text-center">
            Find Us on Map
          </h2>
          <p className="text-stone-600 text-center mb-8">
            Located in the heart of Yusuf Sarai Market, South Delhi
          </p>
        </div>
        <div className="w-full h-96 md:h-[500px]">
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3504.4768478396877!2d77.20494969999999!3d28.5609763!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390ce3a502df49df%3A0x9bcce61a3cb5d4de!2sAggarwal%20Foam%20Palace!5e0!3m2!1sen!2sin!4v1703000000000!5m2!1sen!2sin"
            width="100%"
            height="100%"
            style={{ border: 0 }}
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title="Aggarwal Foam Palace Location"
            data-testid="contact-map"
          />
        </div>
      </section>
    </main>
  );
};

export default ContactPage;
